/**
 * Product Attributes Variant Handler
 *
 * Custom element that handles variant-specific metafield display.
 * Subscribes to variant change events and updates EAN and Variant Color fields.
 *
 * @version 1.3.0
 */
if (!window.ProductAttributesVariant) {
window.ProductAttributesVariant = class ProductAttributesVariant extends HTMLElement {
  connectedCallback() {
    // Defer initialization to next frame to ensure all child elements are parsed
    // This fixes timing issue where connectedCallback fires before JSON script is in DOM
    requestAnimationFrame(() => this.initialize());
  }

  initialize() {
    // Parse variant data - use product-specific cache to handle SPA navigation
    const jsonScript = this.querySelector('[data-variant-attributes]') ||
                       document.querySelector('[data-variant-attributes]');
    const productId = jsonScript?.dataset?.productId;

    console.log('[ProductAttributes] Initializing for product:', productId);

    // Use product-specific cache key
    const cacheKey = `__productAttributesVariantData_${productId}`;

    // Check if we have valid cached data (must have variants)
    const cachedData = productId ? window[cacheKey] : null;
    const hasValidCache = cachedData && Object.keys(cachedData.variants || {}).length > 0;

    // Parse fresh if no cache or cache is empty
    if (!hasValidCache) {
      console.log('[ProductAttributes] Parsing fresh (no valid cache)');
      const parsedData = this.parseVariantData();

      // Only cache if we got valid data
      if (productId && Object.keys(parsedData.variants || {}).length > 0) {
        window[cacheKey] = parsedData;
        console.log('[ProductAttributes] Cached data for product:', productId);
      }
      this.variantData = parsedData;
    } else {
      console.log('[ProductAttributes] Using cached data for product:', productId);
      this.variantData = cachedData;
    }

    console.log('[ProductAttributes] Variant data:', this.variantData);

    // Listen to native variant:changed event (most reliable)
    // Use a bound handler stored on the instance for proper cleanup
    this._boundHandler = this.handleNativeVariantChange.bind(this);
    document.addEventListener('variant:changed', this._boundHandler);

    // Also try FoxTheme pubsub as backup
    this.initPubsubSubscription();

    // Initial display for URL-specified variant or first available
    this.initializeVariantDisplay();

    // Hide product-level color if any variant has color data
    this.hideProductColorIfVariantColorsExist();
  }

  disconnectedCallback() {
    if (this._boundHandler) {
      document.removeEventListener('variant:changed', this._boundHandler);
    }
  }

  /**
   * Initialize pubsub subscription with retry
   */
  initPubsubSubscription() {
    if (window.FoxTheme?.pubsub?.subscribe) {
      FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
        this.handlePubsubVariantChange.bind(this)
      );
    } else {
      // Retry after a short delay if FoxTheme not ready
      setTimeout(() => {
        if (window.FoxTheme?.pubsub?.subscribe) {
          FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
            this.handlePubsubVariantChange.bind(this)
          );
        }
      }, 500);
    }
  }

  /**
   * Parse variant metafield data from embedded JSON
   * Searches within this element first, then document-wide
   */
  parseVariantData() {
    try {
      // First try within this element
      let jsonScript = this.querySelector('[data-variant-attributes]');
      console.log('[ProductAttributes] JSON in element:', jsonScript ? 'found' : 'not found');

      // Fallback to document-wide search
      if (!jsonScript) {
        jsonScript = document.querySelector('[data-variant-attributes]');
        console.log('[ProductAttributes] JSON in document:', jsonScript ? 'found' : 'not found');
      }

      if (jsonScript) {
        const rawContent = jsonScript.textContent || '{}';
        console.log('[ProductAttributes] Raw JSON content:', rawContent.substring(0, 200));
        const data = JSON.parse(rawContent);
        console.log('[ProductAttributes] Parsed JSON:', data);
        console.log('[ProductAttributes] Variant count:', Object.keys(data.variants || {}).length);
        return data;
      } else {
        console.warn('[ProductAttributes] No JSON script found anywhere!');
      }
    } catch (e) {
      console.warn('ProductAttributesVariant: Failed to parse variant data', e);
    }
    return { variants: {} };
  }

  /**
   * Initialize display for the initially selected variant
   */
  initializeVariantDisplay() {
    const variantId = this.getInitialVariantId();
    if (variantId) {
      this.updateVariantFields(variantId);
    }
  }

  /**
   * Hide product-level color field if any variant has color metafields
   * This prevents showing redundant/conflicting color info
   */
  hideProductColorIfVariantColorsExist() {
    const variants = this.variantData.variants || {};
    const colorFields = ['variant_primary_color', 'variant_secondary_color', 'variant_cushion_color'];

    // Check if ANY variant has ANY color data
    const hasAnyVariantColor = Object.values(variants).some(variant =>
      colorFields.some(field => variant[field] && variant[field] !== null)
    );

    if (hasAnyVariantColor) {
      // Hide product-level color fields that have variant-level equivalents
      const productColorSelectors = [
        '[data-product-field="shopify.furniture-fixture-color"]',
        '[data-product-field="custom.cushion_color"]',
        '[data-product-field="custom.cushion_colors"]'
      ];
      productColorSelectors.forEach(selector => {
        const row = this.querySelector(selector);
        if (row) {
          row.style.display = 'none';
          console.log(`[ProductAttributes] Hiding ${selector} - variant colors exist`);
        }
      });
    }
  }

  /**
   * Get initial variant ID from URL or product data
   */
  getInitialVariantId() {
    // Check URL for variant parameter
    const urlParams = new URLSearchParams(window.location.search);
    const urlVariantId = urlParams.get('variant');
    if (urlVariantId) {
      return urlVariantId;
    }

    // Try to get from variant picker's data attribute
    const variantPicker = document.querySelector('variant-selects [data-selected-variant], variant-radios [data-selected-variant]');
    if (variantPicker) {
      try {
        const selectedVariant = JSON.parse(variantPicker.textContent || '{}');
        if (selectedVariant?.id) {
          return String(selectedVariant.id);
        }
      } catch (e) {
        // Silent fail
      }
    }

    return null;
  }

  /**
   * Handle native variant:changed event
   */
  handleNativeVariantChange(event) {
    const variant = event.detail?.variant;
    if (variant?.id) {
      this.updateVariantFields(String(variant.id));
    }
  }

  /**
   * Handle pubsub variant change event
   */
  handlePubsubVariantChange({ data }) {
    const variant = data?.variant;
    if (variant?.id) {
      this.updateVariantFields(String(variant.id));
    }
  }

  /**
   * Update all variant fields with new variant data
   */
  updateVariantFields(variantId) {
    console.log('[ProductAttributes] Updating for variant:', variantId);
    console.log('[ProductAttributes] Available variants:', Object.keys(this.variantData.variants || {}));

    const variantAttrs = this.variantData.variants?.[variantId];
    console.log('[ProductAttributes] Variant data found:', variantAttrs);

    // Update EAN field
    this.updateField('ean', variantAttrs?.ean);

    // Update series field
    this.updateField('series', variantAttrs?.series);

    // Update color fields (primary, secondary, cushion)
    this.updateField('variant_primary_color', variantAttrs?.variant_primary_color);
    this.updateField('variant_secondary_color', variantAttrs?.variant_secondary_color);
    this.updateField('variant_cushion_color', variantAttrs?.variant_cushion_color);
  }

  /**
   * Update a single variant field
   */
  updateField(fieldKey, value) {
    const row = this.querySelector(`[data-variant-field="${fieldKey}"]`);
    if (!row) return;

    const valueEl = row.querySelector('.product-attributes__value');
    if (!valueEl) return;

    if (value && value !== 'null' && value !== null) {
      row.style.display = '';
      valueEl.innerHTML = this.formatValue(fieldKey, value);
    } else {
      row.style.display = 'none';
    }
  }

  /**
   * Format value based on field type
   */
  formatValue(fieldKey, value) {
    // All color fields use the same rendering
    if (fieldKey === 'variant_primary_color' ||
        fieldKey === 'variant_secondary_color' ||
        fieldKey === 'variant_cushion_color') {
      return this.renderColorValue(value);
    }
    // For EAN and other simple values
    return this.escapeHtml(String(value));
  }

  /**
   * Render color value with swatch
   * Handles simplified color structure: { name: string, hex: string }
   */
  renderColorValue(colorData) {
    if (!colorData) return '';

    // Handle single color object or array
    const colors = Array.isArray(colorData) ? colorData : [colorData];

    return colors.map(color => {
      const name = color?.name || '';
      const hex = color?.hex || '';

      if (hex) {
        // Match the nested structure used by product-attribute-value.liquid
        return `<span class="product-attributes__color-swatch"><span class="product-attributes__swatch"><span class="product-attributes__swatch-color" style="background-color: ${this.escapeHtml(hex)};"></span></span> ${this.escapeHtml(name)}</span>`;
      }
      return this.escapeHtml(name);
    }).join(', ');
  }

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
};
} // Close if (!window.ProductAttributesVariant)

// Register custom element
if (!customElements.get('product-attributes-variant')) {
  customElements.define('product-attributes-variant', window.ProductAttributesVariant);
}
